import React, { useEffect, useState } from 'react';
import { SerializedWorkSummary } from '../components/SerializedWorkSummary';
import { WorkOrderFindingsSummary } from '../components/WorkOrderFindingsSummary';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import HeaderMenu from '../components/HeaderMenu';
import MachineryPicker from '../components/MachineryPicker';
import { getStatusMeta, formatDuration, formatDurationLong } from '../lib/serialized';

/**
 * Detail page for a single serialized component. Emphasises "time in service"
 * (the primary business metric) with a big hero number, then the full service
 * event log so higher-ups can see every install and every removal.
 */
const SerializedComponentDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [applicableIds, setApplicableIds] = useState([]);
  // Machinery lookup for the "Currently installed on <equipment name>" hint
  // on the hero card. Cheap — one small fetch per page load.
  const [machineryById, setMachineryById] = useState({});
  const isAdmin = user.role === 'admin' || user.role === 'technician';

  const fetchData = async () => {
    try {
      const [scRes, mRes] = await Promise.all([
        axios.get(`${API}/serialized-components/${id}`),
        axios.get(`${API}/machinery`).catch(() => ({ data: [] })),
      ]);
      setData(scRes.data);
      setForm({
        serial_number: scRes.data.serial_number || '',
        name: scRes.data.name || '',
        mfr_part_number: scRes.data.mfr_part_number || '',
        manufacturer: scRes.data.manufacturer || '',
        model: scRes.data.model || '',
        status: scRes.data.status || 'new',
        notes: scRes.data.notes || '',
      });
      setApplicableIds(scRes.data.applicable_machinery_ids || []);
      const mById = {};
      (mRes.data || []).forEach((m) => { mById[m.id] = m; });
      setMachineryById(mById);
    } catch (err) {
      toast.error('Failed to load component');
      navigate('/inventory?tab=serialized');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        serial_number: form.serial_number.trim(),
        name: form.name.trim(),
        mfr_part_number: form.mfr_part_number.trim(),
        manufacturer: form.manufacturer.trim() || null,
        model: form.model.trim() || null,
        applicable_machinery_ids: applicableIds,
        notes: form.notes.trim() || null,
      };
      // Status only sent if the user changed it AND we're not currently installed.
      if (form.status && form.status !== data.status && data.status !== 'installed') {
        payload.status = form.status;
      }
      await axios.put(`${API}/serialized-components/${id}`, payload);
      toast.success('Saved');
      setEditing(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/serialized-components/${id}`);
      toast.success('Deleted');
      navigate('/inventory?tab=serialized');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading…</div>
      </div>
    );
  }
  if (!data) return null;

  const meta = getStatusMeta(data.status);
  const tis = data.time_in_service || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/inventory?tab=serialized')} data-testid="back-btn">
                ← Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" data-testid="sc-name">
                  {data.name}
                </h1>
                <p className="text-xs text-slate-600 font-mono">
                  SN: <span data-testid="sc-serial">{data.serial_number}</span>
                  {data.mfr_part_number && <> · MFR PN: <span data-testid="sc-mfr-pn">{data.mfr_part_number}</span></>}
                  {data.manufacturer && <> · {data.manufacturer}</>}
                  {data.model && <> · {data.model}</>}
                </p>
              </div>
            </div>
            <HeaderMenu user={user} onLogout={onLogout} />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Hero: Time in Service — the whole reason this feature exists. */}
        <Card data-testid="tis-hero-card">
          <CardContent className="py-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Total Time in Service
                </div>
                <div className="text-4xl font-bold text-slate-900" data-testid="tis-total">
                  {formatDuration(tis.total_seconds)}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {formatDurationLong(tis.total_seconds)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Install Count
                </div>
                <div className="text-4xl font-bold text-slate-900" data-testid="tis-install-count">
                  {tis.install_count || 0}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Times placed at an installation point
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Status
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${meta.className} text-sm px-2 py-1`} data-testid="sc-status-badge">
                    {meta.label}
                  </Badge>
                  {data.status === 'installed' && data.current_machinery_id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/machinery/${data.current_machinery_id}`)}
                      className="text-sm font-semibold text-slate-900 hover:underline"
                      data-testid="sc-current-equipment"
                      title="Go to equipment page"
                    >
                      on {machineryById[data.current_machinery_id]?.component || '(unknown equipment)'}
                    </button>
                  )}
                </div>
                {data.status === 'installed' && data.current_installed_at && (
                  <div className="text-xs text-slate-600 mt-2" data-testid="sc-current-install-line">
                    Currently installed · since{' '}
                    {new Date(data.current_installed_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details / Edit */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Details</CardTitle>
              {isAdmin && !editing && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="sc-edit-btn">
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="sc-delete-btn"
                        disabled={data.status === 'installed'}
                        title={data.status === 'installed' ? 'Remove from equipment first' : 'Delete'}>
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this serialized component?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove the component AND its entire service
                          history. This is a permanent operation — prefer
                          setting the status to Decommissioned if you want to
                          retire the part while keeping its history.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} data-testid="sc-confirm-delete-btn">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editing ? (
              <form onSubmit={handleSave} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ed-serial">Serial Number *</Label>
                    <Input id="ed-serial" value={form.serial_number}
                      onChange={(e) => setForm({ ...form, serial_number: e.target.value })} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ed-name">Part Name *</Label>
                    <Input id="ed-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ed-mfr-pn">MFR Part Number *</Label>
                  <Input
                    id="ed-mfr-pn"
                    data-testid="ed-mfr-pn-input"
                    value={form.mfr_part_number}
                    onChange={(e) => setForm({ ...form, mfr_part_number: e.target.value })}
                    placeholder='e.g. "CV-10-316"'
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ed-mfr">Manufacturer</Label>
                    <Input id="ed-mfr" value={form.manufacturer}
                      onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ed-model">Model</Label>
                    <Input id="ed-model" value={form.model}
                      onChange={(e) => setForm({ ...form, model: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Applies to equipment</Label>
                  <MachineryPicker
                    value={applicableIds}
                    onChange={setApplicableIds}
                  />
                  <p className="text-xs text-slate-500">
                    Leave empty if the part fits anywhere. Selecting equipment
                    restricts the Install picker so wrong-fit parts stay out.
                  </p>
                </div>
                {data.status !== 'installed' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="ed-status">Status</Label>
                    <select
                      id="ed-status"
                      data-testid="sc-edit-status-select"
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                    >
                      <option value="new">New</option>
                      <option value="available">Available</option>
                      <option value="awaiting_rebuild">Awaiting Rebuild</option>
                      <option value="decommissioned">Decommissioned</option>
                    </select>
                    <p className="text-xs text-slate-500">
                      Rebuild finished? Flip Awaiting Rebuild → Available so it can be installed again.
                    </p>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="ed-notes">Notes</Label>
                  <Textarea id="ed-notes" rows={3} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button type="submit" data-testid="sc-save-btn">Save</Button>
                </div>
              </form>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <dt className="text-slate-500 text-xs">Serial Number</dt>
                  <dd className="font-mono">{data.serial_number}</dd>
                </div>
                <div>
                  <dt className="text-slate-500 text-xs">Part Name</dt>
                  <dd>{data.name}</dd>
                </div>
                <div>
                  <dt className="text-slate-500 text-xs">MFR Part Number</dt>
                  <dd className="font-mono">{data.mfr_part_number || <span className="text-slate-400 italic font-sans">—</span>}</dd>
                </div>
                <div>
                  <dt className="text-slate-500 text-xs">Manufacturer</dt>
                  <dd>{data.manufacturer || <span className="text-slate-400 italic">—</span>}</dd>
                </div>
                <div>
                  <dt className="text-slate-500 text-xs">Model</dt>
                  <dd>{data.model || <span className="text-slate-400 italic">—</span>}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-slate-500 text-xs">Applies to equipment</dt>
                  <dd data-testid="sc-applies-to">
                    {(data.applicable_machinery_ids && data.applicable_machinery_ids.length > 0) ? (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {data.applicable_machinery_ids.map((mid) => (
                          <button
                            key={mid}
                            type="button"
                            onClick={() => navigate(`/machinery/${mid}`)}
                            className="text-xs bg-slate-100 hover:bg-slate-200 rounded px-2 py-0.5"
                          >
                            {machineryById[mid]?.component || '(unknown)'}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-500 italic">Any equipment</span>
                    )}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-slate-500 text-xs">Notes</dt>
                  <dd className="whitespace-pre-wrap">{data.notes || <span className="text-slate-400 italic">—</span>}</dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>

        {/* Service history / biography — install/remove spans interleaved
            with every maintenance log + completed WO that touched this
            component's installation point while it was installed. */}
        <Card>
          <CardHeader>
            <CardTitle>Service History</CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.timeline || data.timeline.length === 0) ? (
              <div className="text-sm text-slate-500 italic py-4">
                No service events yet. This part has never been installed.
              </div>
            ) : (
              <div className="divide-y" data-testid="sc-events-list">
                {data.timeline.map((row) => {
                  // Two shapes flow through this list — install/remove spans
                  // (kind === 'service_span') and work events (log/WO).
                  if (row.kind === 'service_span') {
                    const ev = row;
                    return (
                      <div key={ev.id} className="py-3 flex flex-col sm:flex-row sm:justify-between gap-2"
                           data-testid={`sc-event-${ev.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {ev.is_open ? (
                              <Badge className="bg-blue-600 text-white">Currently installed</Badge>
                            ) : (
                              <Badge className="bg-slate-200 text-slate-800">Closed</Badge>
                            )}
                            <span className="font-medium text-sm">{ev.equipment_name}</span>
                            <span className="text-xs text-slate-500">@</span>
                            <span className="text-sm">{ev.installation_point_label}</span>
                          </div>
                          <div className="text-xs text-slate-600 mt-1">
                            Installed {new Date(ev.installed_at).toLocaleString()} by {ev.installed_by_name}
                            {ev.removed_at && (
                              <> · Removed {new Date(ev.removed_at).toLocaleString()} by {ev.removed_by_name}</>
                            )}
                          </div>
                          {ev.removal_reason && (
                            <div className="text-xs text-slate-700 mt-1">
                              <span className="text-slate-500">Reason:</span> {ev.removal_reason}
                            </div>
                          )}
                          {ev.notes && (
                            <div className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">
                              <span className="text-slate-500">Notes:</span> {ev.notes}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs text-slate-500">Duration</div>
                          <div className="text-lg font-semibold text-slate-900">
                            {formatDuration(Math.floor((ev.span_ms || 0) / 1000))}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Work-while-installed row (maintenance log or completed WO).
                  const isWO = row.kind === 'completed_work_order';
                  const badgeClass = isWO
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    : 'bg-amber-100 text-amber-900 border border-amber-200';
                  const badgeText = isWO
                    ? (row.wo_number ? `Work Order #${row.wo_number}` : 'Work Order')
                    : 'Maintenance Log';
                  return (
                    <div key={row.id} className="py-3 flex flex-col sm:flex-row sm:justify-between gap-2"
                         data-testid={`sc-event-${row.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={badgeClass}>{badgeText}</Badge>
                          <button
                            type="button"
                            onClick={() => row.machinery_id && navigate(`/machinery/${row.machinery_id}`)}
                            className="font-medium text-sm hover:underline text-left"
                            data-testid={`sc-event-equipment-${row.id}`}
                            title="Open equipment page"
                          >
                            {row.equipment_name}
                          </button>
                          <span className="text-xs text-slate-500">@</span>
                          <span className="text-sm">{row.installation_point_label}</span>
                        </div>
                        <div className="text-sm font-semibold text-slate-900 mt-1">{row.title}</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {new Date(row.timeline_at).toLocaleString()}
                          {row.actor_name && <> · {row.actor_name}</>}
                          {row.hours_spent != null && <> · {row.hours_spent} hr</>}
                        </div>
                        {row.resolution && (
                          <div className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">
                            <span className="text-slate-500">Resolution:</span> {row.resolution}
                          </div>
                        )}
                        {row.description && row.description !== row.title && <p className="text-sm whitespace-pre-wrap break-words mt-2" data-testid={`sc-work-description-${row.id}`}>{row.description}</p>}
                        <SerializedWorkSummary records={row.serialized_work} testId={`sc-work-summary-${row.id}`} />
                        <WorkOrderFindingsSummary findings={row.findings} followUps={row.follow_ups} machineryId={row.machinery_id} testId={`sc-findings-${row.id}`} />
                        {row.parts_used && (
                          <div className="text-xs text-slate-700 mt-1">
                            <span className="text-slate-500">Parts:</span> {row.parts_used}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SerializedComponentDetail;
